import { RD_PRODUCT_CODE } from '@/app/config/products.config';
import { useCanManualEmission, useProductEmissionMode } from '@/shared/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import { useState } from 'react';
import { isAwaitingManualEmission } from '../utils/emission-control.util';
import { useIntegrationDetails } from './use-integration-details.hook';
import { useManualEmission } from './use-manual-emission.hook';

interface UseManualEmissionActionParams {
  policyId: string;
  /**
   * Só para o `emissionMode` devolvido (textos da tela, como no RD); NÃO decide
   * a visibilidade do botão.
   */
  productCode?: string | null;
  /**
   * Condição da própria tela (ex.: a proposta está transmitida). O estado do
   * controle de emissão é verificado aqui dentro.
   */
  isEligible?: boolean;
}

/**
 * Orquestra o botão "Emitir manualmente" — visibilidade, popup e chamada à API.
 *
 * Fica num hook, e não num componente, porque as duas telas onde o botão aparece
 * têm linguagens visuais diferentes (a de Integrações usa um botão compacto ao
 * lado do "Reprocessar"; a da proposta usa o botão do design system). Cada tela
 * renderiza o seu gatilho e monta o mesmo modal.
 *
 * `isVisible` exige perfil Administrador, a tela elegível e o controle
 * AGUARDANDO emissão manual. O modo ATUAL do produto não entra na conta: a
 * proposta transmitida enquanto ele estava em MANUAL continua aceitando o número
 * depois que a área volta o produto para INTEGRATION.
 */
export function useManualEmissionAction({
  policyId,
  productCode,
  isEligible = true,
}: UseManualEmissionActionParams) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { canManualEmission } = useCanManualEmission();
  const { emissionMode } = useProductEmissionMode(productCode);

  // Só consulta o controle para quem poderia ver o botão — evita pedir
  // `policies.integrations.read` a quem não é Administrador.
  const shouldCheckControl = canManualEmission === true && isEligible;
  const { data: control } = useIntegrationDetails(policyId, {
    enabled: shouldCheckControl,
  });

  const mutation = useManualEmission();

  const isVisible = shouldCheckControl && isAwaitingManualEmission(control);

  // PDF da apólice obrigatório fora do RD Equipamentos, que não tem PDF nem
  // ECM no Portal (a Api aplica a mesma regra).
  const requiresPolicyPdf =
    (productCode ?? control?.productCode) !== RD_PRODUCT_CODE;

  const open = () => {
    setErrorMessage(null);
    setIsOpen(true);
  };

  const close = () => {
    if (mutation.isPending) return;
    setErrorMessage(null);
    setIsOpen(false);
  };

  /** Erro de negócio fica no modal; sucesso fecha e avisa por toast. */
  const confirm = async (i4proPolicyNo: string, policyPdf?: File | null) => {
    setErrorMessage(null);

    try {
      const response = await mutation.mutateAsync({
        policyId,
        i4proPolicyNo,
        policyPdf,
      });

      if (!response.success) {
        setErrorMessage(
          response.error || 'Não foi possível vincular a apólice informada.'
        );
        return;
      }

      setIsOpen(false);
      toaster.create({
        type: 'success',
        title: 'Emissão manual concluída',
        description:
          response.data?.message ??
          `Apólice ${i4proPolicyNo} vinculada e emissão concluída.`,
      });
    } catch (error) {
      // Falha de transporte (rede/CORS): a mutation não lança em erro de negócio.
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Não foi possível concluir a emissão manual.'
      );
    }
  };

  return {
    isVisible,
    /**
     * `undefined` enquanto `/settings/products` ainda carrega — quem exibir
     * texto condicionado ao modo (ex.: timeline/alert de "aguardando emissão")
     * deve tratar `undefined` como "ainda não sei" e não afirmar nem manual
     * nem automático, pelo mesmo motivo que `isVisible` some nesse intervalo.
     */
    emissionMode,
    requiresPolicyPdf,
    isOpen,
    open,
    close,
    confirm,
    isPending: mutation.isPending,
    errorMessage,
  };
}
